import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
	Input,
	Button,
	Checkbox,
	FormItem,
	FormContainer,
	Alert,
} from 'components/ui';
import { PasswordInput, ActionLink } from 'components/shared';

import useTimeOutMessage from 'utils/hooks/useTimeOutMessage';
import useAuth from 'utils/hooks/useAuth';

const SignInForm = props => {
	const { t } = useTranslation();

	const {
		disableSubmit = false,
		className,
		forgotPasswordUrl = '/forgot-password',
	} = props;

	const [message, setMessage] = useTimeOutMessage();

	const validationSchema = Yup.object().shape({
		email: Yup.string()
			.email(t('input.invalidEmail'))
			.required(t('input.enterYourEmail')),
		password: Yup.string().required(t('input.enterYourPassword')),
		rememberMe: Yup.bool(),
	});

	const { signIn } = useAuth();

	const onSignIn = async (values, setSubmitting) => {
		const { email, password } = values;
		setSubmitting(true);

		const result = await signIn({ email, password });

		if (result.status === 'failed') setMessage(result.message);

		setSubmitting(false);
	};

	return (
		<div className={className}>
			{message && (
				<Alert className="mb-4" type="danger" showIcon closable>
					{message}
				</Alert>
			)}
			<Formik
				initialValues={{
					email: '',
					password: '',
					rememberMe: true,
				}}
				validationSchema={validationSchema}
				onSubmit={(values, { setSubmitting }) => {
					if (!disableSubmit) {
						onSignIn(values, setSubmitting);
					} else {
						setSubmitting(false);
					}
				}}>
				{({ touched, errors, isSubmitting }) => (
					<Form>
						<FormContainer>
							<FormItem
								label={t('input.email')}
								invalid={errors.email && touched.email}
								errorMessage={errors.email}>
								<Field
									type="email"
									autoComplete="on"
									name="email"
									component={Input}
								/>
							</FormItem>
							<FormItem
								label={t('input.password')}
								invalid={errors.password && touched.password}
								errorMessage={errors.password}>
								<Field
									autoComplete="on"
									name="password"
									component={PasswordInput}
								/>
							</FormItem>
							<div className="flex justify-between mt-6 mb-6">
								<Field
									className="mb-0"
									name="rememberMe"
									component={Checkbox}
									children={t('authentication.signin.rememberMe')}
								/>
								<ActionLink to={forgotPasswordUrl}>
									{t('authentication.signin.forgotPassword')}
								</ActionLink>
							</div>
							<Button
								block
								loading={isSubmitting}
								variant="solid"
								type="submit">
								{isSubmitting
									? t('authentication.signin.signInAccount')
									: t('authentication.signin.title')}
							</Button>
						</FormContainer>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default SignInForm;
